import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { GET_EMPTY_BOXES } from '../../../graphql/client/queries';
import { LoadStatusEnum } from '../../../types/LoadStatusEnum';
import { PermissionEnum } from '../../../types/PermissionEnum';
import {
    GetEmptyBoxes,
    GetEmptyBoxes_getEmptyBoxes_emptyBoxDetails as EmptyBoxes,
    GetLoadDetail_getLoadDetail as LoadDetail,
    GetLoadProgress_getLoadProgress as LoadProgress,
} from '../../../types/schemaTypes';
import { useAuthContext } from '../../Auth/AuthProvider';
import { Option } from '../../Users/interface';
import { CustomButton, DateTimePicker } from '../../_common';
import SearchableDropdown from '../../_common/SearchableDropdown/SearchableDropdown';
import TextContent from '../../_common/TextContent';
import { Textfield } from '../../_common/Textfield/Textfield';
import './EditLoadProgress.scss';
import useEditLoadProgress from './useEditLoadProgress';

interface EditLoadProgressProps {
    onClickShow: () => void;
    onClickHide: () => void;
    show: boolean;
    loadStatus: Option[];
    loadProgress: LoadProgress;
    loadDetail: LoadDetail;
}

const EditLoadProgress = ({
    onClickHide,
    show,
    loadStatus,
    loadProgress,
    loadDetail,
}: EditLoadProgressProps): React.ReactElement => {
    const emptyBoxes: Option[] = [];
    const [hasConcurrentError, setHasConcurrentError] = useState(false);
    const [backendErrorMessage, setBackendErrorMessage] = useState('');
    const onUpdateError = (message?: string): void => {
        if (message && (message.includes('Load weight cannot be less than allocated weight') 
            || message.includes('Load is currently allocated.'))) {
            setBackendErrorMessage(message);
            setHasConcurrentError(false);
            return;
        }
        setBackendErrorMessage('');
        setHasConcurrentError(true);
    };
    const {
        handleSubmit,
        handleDropdownChange,
        handleAutoCompleteChange,
        handleInputChange,
        handleDateInputChange,
        inputs,
        fieldErrors,
        validateLofaDateIn,
        validateLofaDateOut,
        validateDeliveryDateIn,
        validateDeliveryDateOut,
        validateSandTicket,
        validateAdditionalSandTicket,
        validateSandboxBOL,
        validateWeight,
        validateGrossWeight,
        validateTareWeight,
        validateMiles,
        validateEmptyBox1,
        validateEmptyBox2,
        handleCancel,
        loadingErrorMessage,
        loadedErrorMessage,
        onSiteErrorMessage,
        deliveredErrorMessage,
        sandTicketNumberErrorMessage,
        bolNumberErrorMessage,
        otherTicketNumberErrorMessage,
        weightErrorMessage,
        grossWeightErrorMessage,
        tareWeightErrorMessage,
        milesToWellErrorMessage,
        emptyBoxOneErrorMessage,
        emptyBoxTwoErrorMessage,
        permissionError,
    } = useEditLoadProgress(loadProgress, loadDetail.loadId, onClickHide, onUpdateError, loadDetail.status);

    const timeConverter = (minutes: string | null): string => {
        if (minutes != null) {
            const hours = parseInt(minutes) / 60;
            const rhours = Math.floor(hours);
            const min = (hours - rhours) * 60;
            const rmin = Math.round(min);
            return rhours + ' Hour,' + ' ' + rmin + ' minutes';
        } else {
            return '';
        }
    };
    const { data: emptyBoxesData } = useQuery<GetEmptyBoxes>(GET_EMPTY_BOXES, {
        variables: {
            jobId: 0,
        },
        fetchPolicy: 'network-only',
    });

    if (emptyBoxesData?.getEmptyBoxes?.emptyBoxDetails) {
        (emptyBoxesData.getEmptyBoxes.emptyBoxDetails as (EmptyBoxes | null)[]).forEach((element: EmptyBoxes | null): void => {
            if (element != null) {
                emptyBoxes.push({
                    id: element.boxId ?? 0,
                    name: element.boxDesc ?? '',
                });
            }
        });
    }
    const searchIcon = <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} />;

    const canEditLoadProgress = useAuthContext().userHasPermission(PermissionEnum.EditLoadProgress);
    const canEditLoadTimestamps = useAuthContext().userHasPermission(PermissionEnum.EditLoadTimestamps);
    const canEditCompletedLoads = useAuthContext().userHasPermission(PermissionEnum.EditCompletedLoads);

    const checkForErrors = (): boolean => {
        if (
            fieldErrors.lofaTimeInError !== '' ||
            fieldErrors.lofaTimeOutError !== '' ||
            fieldErrors.deliveryTimeInError !== '' ||
            fieldErrors.deliveryTimeOutError !== '' ||
            fieldErrors.lofaDateInError !== '' ||
            fieldErrors.lofaDateOutError !== '' ||
            fieldErrors.deliveryDateInError !== '' ||
            fieldErrors.deliveryDateOutError !== '' ||
            loadingErrorMessage !== '' ||
            loadedErrorMessage !== '' ||
            onSiteErrorMessage !== '' ||
            deliveredErrorMessage !== '' ||
            sandTicketNumberErrorMessage !== '' ||
            bolNumberErrorMessage !== '' ||
            weightErrorMessage !== '' ||
            grossWeightErrorMessage !== '' ||
            tareWeightErrorMessage !== '' ||
            milesToWellErrorMessage !== '' ||
            otherTicketNumberErrorMessage !== '' ||
            emptyBoxOneErrorMessage !== '' ||
            emptyBoxTwoErrorMessage !== ''
        ) {
            return true;
        }
        return false;
    };

    return (
        <Modal
            className="EditLoadProgress-Modal"
            show={show}
            onHide={() => {
                handleCancel();
                onClickHide();
                setHasConcurrentError(false);
                setBackendErrorMessage('');
            }}
            size="lg"
        >
            <Modal.Header className="EditLoad-EditHeader">
                <div className="EditLoad-HeaderLeft">
                    <i className="material-icons EditLoad-EditIcon">create</i>
                    <span className="EditLoad-EditLink">Edit</span>
                    <span className="EditLoad-TitleText">Load Progress</span>
                    <span className="EditLoad-TitleTextError">
                        {backendErrorMessage ? (
                            <span>{backendErrorMessage}</span>
                        ) : hasConcurrentError ? (
                            <span>
                                An edit has already been made for this load. Refresh the load profile to try again.
                            </span>
                        ) : (
                            permissionError
                        )}
                    </span>
                </div>
                <div className="EditLoad-HeaderRight">
                    {hasConcurrentError && (
                        <div className="EditLoad-ButtonWrapper">
                            <CustomButton
                                title="Refresh"
                                isBordered
                                onClick={() => window.location.reload()}
                            >
                                <i className="material-icons">refresh</i>
                            </CustomButton>
                        </div>
                    )}
                    <div className="EditLoad-ButtonWrapper">
                        <CustomButton
                            isBordered={true}
                            title="Cancel"
                            onClick={() => {
                                handleCancel();
                                onClickHide();
                                setHasConcurrentError(false);
                                setBackendErrorMessage('');
                            }}
                        />
                    </div>
                    <div className="EditLoad-ButtonWrapper">
                        <CustomButton
                            isBordered={false}
                            isDisabled={checkForErrors()}
                            title="Save"
                            onClick={handleSubmit}
                        />
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className="EditLoad-Border">
                    <div className="InitialModal-Body">
                        <div className="EditLoad-BodyLeftPadding">
                            <div className="EditLoad-BodyLeft">
                                <div
                                    className={` ${!canEditCompletedLoads &&
                                        loadDetail.status === LoadStatusEnum.COMPLETED &&
                                        'EditLoad-BodyLeft-Disabled'}`}
                                >
                                    <Row>
                                        <Col md={10}>
                                            <SearchableDropdown
                                                dropdownOptions={loadStatus}
                                                id="loadStatus"
                                                label="LOAD STATUS"
                                                onSelect={(eventKey: string | null) => {
                                                    handleDropdownChange('loadStatus', eventKey);
                                                }}
                                                value={loadDetail.status}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                                <div
                                    className={` ${!canEditLoadProgress &&
                                        'EditLoad-BodyLeft-Disabled'} ${loadDetail.status ===
                                        LoadStatusEnum.COMPLETED && 'EditLoad-BodyLeft-Disabled'}`}
                                >
                                    <Row>
                                        <Col md={10} className="EditLoadProgress-MilesError">
                                            <Textfield
                                                label="MILES"
                                                name="milesToWell"
                                                value={inputs.milesToWell}
                                                onChange={event => {
                                                    handleInputChange(event);
                                                    validateMiles(event.target.value);
                                                }}
                                                error={milesToWellErrorMessage !== ''}
                                                helperText={milesToWellErrorMessage}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={10} className="EditLoadProgress-MilesError">
                                            <Textfield
                                                label="WEIGHT"
                                                name="weight"
                                                value={inputs.weight}
                                                onChange={event => {
                                                    handleInputChange(event);
                                                    validateWeight(event.target.value);
                                                }}
                                                error={weightErrorMessage !== ''}
                                                helperText={weightErrorMessage}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={10} className="EditLoadProgress-MilesError">
                                            <Textfield
                                                label="GROSS WEIGHT"
                                                name="grossWeight"
                                                value={inputs.grossWeight}
                                                onChange={event => {
                                                    handleInputChange(event);
                                                    validateGrossWeight(event.target.value);
                                                }}
                                                error={grossWeightErrorMessage !== ''}
                                                helperText={grossWeightErrorMessage}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={10} className="EditLoadProgress-MilesError">
                                            <Textfield
                                                label="TARE WEIGHT"
                                                name="tareWeight"
                                                value={inputs.tareWeight}
                                                onChange={event => {
                                                    handleInputChange(event);
                                                    validateTareWeight(event.target.value);
                                                }}
                                                error={tareWeightErrorMessage !== ''}
                                                helperText={tareWeightErrorMessage}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={10} className="Empty-Box">
                                            <SearchableDropdown
                                                autoCompleteEnabled={true}
                                                dropdownOptions={emptyBoxes}
                                                errorMessage={fieldErrors.emptyBoxNumber1Error}
                                                hasError={fieldErrors.emptyBoxNumber1Error !== ''}
                                                id="emptyBoxNumber1"
                                                label="EMPTY BOX #1"
                                                onSelect={(eventKey: string | null) => {
                                                    handleAutoCompleteChange('emptyBoxNumber1', {
                                                        id: eventKey,
                                                        name: inputs.emptyBoxes,
                                                    });
                                                }}
                                                value={inputs.emptyBox1}
                                                endIcon={searchIcon}
                                                disabled={loadDetail.isBellyDump? loadDetail.isBellyDump: false}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={10} className="Empty-Box">
                                            <SearchableDropdown
                                                autoCompleteEnabled={true}
                                                dropdownOptions={emptyBoxes}
                                                errorMessage={fieldErrors.emptyBoxNumber2Error}
                                                hasError={fieldErrors.emptyBoxNumber2Error !== ''}
                                                id="emptyBoxNumber2"
                                                label="EMPTY BOX #2"
                                                onSelect={(eventKey: string | null) => {
                                                    handleAutoCompleteChange('emptyBoxNumber2', {
                                                        id: eventKey,
                                                        name: inputs.emptyBoxes,
                                                    });
                                                }}
                                                value={inputs.emptyBox2}
                                                endIcon={searchIcon}
                                                disabled={loadDetail.isBellyDump? loadDetail.isBellyDump: false}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </div>
                        <div
                            className={`EditLoad-BodyRight ${loadDetail.status === LoadStatusEnum.COMPLETED &&
                                'EditLoad-BodyRight-Disabled'}`}
                        >
                            <Row className="EditLoadProgress-Row">
                                <span className="EditLoad-LoadingFacilityLabelText">At Loading Facility (CST)</span>
                            </Row>
                            <div className={`${!canEditLoadTimestamps && 'EditLoad-BodyRight-Disabled'} `}>
                                <Row className="EditLoadProgress-Row">
                                    <Col md={4}>
                                        <DateTimePicker
                                            label="DATE IN"
                                            value={inputs.loadingDateTime}
                                            error={loadingErrorMessage || fieldErrors.lofaDateInError}
                                            onChange={(value: any) => {
                                                handleDateInputChange('loadingDateTime', value);
                                                validateLofaDateIn(value);
                                            }}
                                        />
                                    </Col>
                                    <Col className="EditLoad-ColDivider" md={4}>
                                        <DateTimePicker
                                            label="DATE OUT"
                                            value={inputs.loadedDateTime}
                                            error={loadedErrorMessage || fieldErrors.lofaDateOutError}
                                            onChange={(value: any) => {
                                                handleDateInputChange('loadedDateTime', value);
                                                validateLofaDateOut(value);
                                            }}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <TextContent
                                            hasTopMargin
                                            title={'Transload Detention: '}
                                            value={timeConverter(loadProgress.loadingElapsedTime)}
                                            color={'darkGreen'}
                                        ></TextContent>
                                    </Col>
                                </Row>
                                <Row className="EditLoadProgress-Row">
                                    <span className="EditLoad-LoadingFacilityLabelText">At Destination (CST)</span>
                                </Row>
                                <Row className="EditLoadProgress-Row">
                                    <Col md={4}>
                                        <DateTimePicker
                                            label="DATE IN"
                                            value={inputs.onSiteDateTime}
                                            error={onSiteErrorMessage || fieldErrors.deliveryDateInError}
                                            onChange={(value: any) => {
                                                handleDateInputChange('onSiteDateTime', value);
                                                validateDeliveryDateIn(value);
                                            }}
                                        />
                                    </Col>

                                    <Col className="EditLoad-ColDivider" md={4}>
                                        <DateTimePicker
                                            label="DATE OUT"
                                            value={inputs.deliveredDateTime}
                                            error={deliveredErrorMessage || fieldErrors.deliveryDateOutError}
                                            onChange={(value: any) => {
                                                handleDateInputChange('deliveredDateTime', value);
                                                validateDeliveryDateOut(value);
                                            }}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <TextContent
                                            hasTopMargin
                                            title={'Well Site Detention: '}
                                            value={timeConverter(loadProgress.deliveryElapsedTime)}
                                            color={'red'}
                                        ></TextContent>
                                    </Col>
                                </Row>
                            </div>
                            <div className={`${!canEditLoadProgress && 'EditLoad-BodyRight-Disabled'} `}>
                                <Row className="EditLoadProgress-Row">
                                    <Col md={4}>
                                        <Textfield
                                            label="SAND TICKET #"
                                            name="sandTicketNumber"
                                            value={inputs.sandTicketNumber}
                                            onChange={event => {
                                                handleInputChange(event);
                                                validateSandTicket(event.target.value);
                                            }}
                                            error={sandTicketNumberErrorMessage !== ''}
                                            helperText={sandTicketNumberErrorMessage}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Textfield
                                            label="SANDBOX BOL #"
                                            name="bolNumber"
                                            value={inputs.bolNumber}
                                            onChange={event => {
                                                handleInputChange(event);
                                                validateSandboxBOL(event.target.value);
                                            }}
                                            error={bolNumberErrorMessage !== ''}
                                            helperText={bolNumberErrorMessage}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Textfield
                                            label="ADDITIONAL SAND TICKET/OTHER #"
                                            name="additionalTicketNumber"
                                            value={inputs.additionalTicketNumber}
                                            onChange={event => {
                                                handleInputChange(event);
                                                validateAdditionalSandTicket(event.target.value);
                                            }}
                                            error={otherTicketNumberErrorMessage !== ''}
                                            helperText={otherTicketNumberErrorMessage}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="InitialModal-Footer"></Modal.Footer>
        </Modal>
    );
};

export default EditLoadProgress;

